/**
 * LeadDetailPage — Full lead detail with 6-tab layout + right action panel.
 *
 * Apr 28, 2026 (Squad-D). Screen 10 of 12.
 *
 * Tabs:
 *  1. Source — payload + auto-extracted KV
 *  2. Conversation — ChatKart transcript
 *  3. Verification trail — timeline of verify events
 *  4. Activities — manager-side activities
 *  5. Assigned-team activity — exec-side activities
 *  6. Reassignment history
 *
 * Right panel:
 *  - Avatar / name / company hint
 *  - Action buttons (Call · WhatsApp · Reassign · Reject)
 *  - Current assignment card
 *  - Tags
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone, MessageSquare, ArrowRight, X, Edit3, ArrowLeft } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  Avatar,
  EmptyState,
  FieldLabel,
  Panel,
  PriorityChip,
  PriorityDot,
  type Lead,
  unwrapList,
  leadName,
  leadPhone,
  leadProduct,
  leadRegion,
  priorityFrom,
  relativeTime,
} from './_lmShared';

type TabKey = 'source' | 'conversation' | 'trail' | 'activities' | 'team-activity' | 'reassign';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'source', label: 'Source' },
  { key: 'conversation', label: 'Conversation' },
  { key: 'trail', label: 'Verification trail' },
  { key: 'activities', label: 'Activities' },
  { key: 'team-activity', label: 'Assigned-team activity' },
  { key: 'reassign', label: 'Reassignment history' },
];

interface TrailEvent {
  id?: string;
  ago?: string;
  who?: string;
  what?: string;
  detail?: string;
  createdAt?: string;
}

interface Activity {
  id?: string;
  who?: string;
  what?: string;
  detail?: string;
  ago?: string;
  createdAt?: string;
}

interface ChatMsg {
  id?: string;
  from?: string;
  text?: string;
  body?: string;
  createdAt?: string;
}

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('source');

  const [trail, setTrail] = useState<TrailEvent[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [teamActivities, setTeamActivities] = useState<Activity[]>([]);
  const [reassignHistory, setReassignHistory] = useState<TrailEvent[]>([]);
  const [transcript, setTranscript] = useState<ChatMsg[]>([]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    safeGet<Lead>(`/leads/${id}`).then((r) => {
      setLead(r.data);
      setLoading(false);
    });
    safeGet<unknown>(`/leads/${id}/verification-trail`).then((r) => {
      setTrail(unwrapList<TrailEvent>(r.data));
    });
    safeGet<unknown>(`/leads/${id}/activities`).then((r) => {
      setActivities(unwrapList<Activity>(r.data));
    });
    safeGet<unknown>(`/leads/${id}/team-activities`).then((r) => {
      setTeamActivities(unwrapList<Activity>(r.data));
    });
    safeGet<unknown>(`/leads/${id}/reassignment-history`).then((r) => {
      setReassignHistory(unwrapList<TrailEvent>(r.data));
    });
    safeGet<unknown>(`/leads/${id}/messages`).then((r) => {
      setTranscript(unwrapList<ChatMsg>(r.data));
    });
  }, [id]);

  const subText = useMemo(() => {
    if (!lead) return '';
    const status = lead.status ?? lead.verificationStatus ?? '—';
    const team = lead.teamName ? ` · Assigned to ${lead.teamName}` : '';
    const exec = lead.assigneeName ? ` / ${lead.assigneeName}` : '';
    return `Status: ${status}${team}${exec}`;
  }, [lead]);

  if (loading) {
    return (
      <div className="aura-root" style={{ padding: 28, color: 'var(--ink-3)' }}>
        Loading lead…
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="aura-root" style={{ padding: 28 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(-1)}>
          <ArrowLeft size={12} /> Back
        </button>
        <div style={{ marginTop: 18, color: 'var(--ink-3)' }}>Lead not found.</div>
      </div>
    );
  }

  return (
    <div
      className="aura-root"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 'calc(100vh - 56px)',
        background: 'var(--ivory-0)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          background: 'var(--ivory-0)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(-1)}
          style={{ flexShrink: 0 }}
        >
          <ArrowLeft size={13} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--ff-serif)',
              fontWeight: 500,
              fontSize: 22,
              color: 'var(--ink-0)',
              letterSpacing: -0.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Lead · {lead.id.slice(-6)} · {leadName(lead)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{subText}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline">
            <Phone size={13} /> Call
          </button>
          <button className="btn btn-outline">
            <Edit3 size={13} /> Edit
          </button>
          <button className="btn btn-outline">
            <ArrowRight size={13} /> Reassign
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Main column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              gap: 0,
              borderBottom: '1px solid var(--line)',
              padding: '0 24px',
              background: 'var(--ivory-0)',
              overflowX: 'auto',
            }}
          >
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: '12px 14px',
                  fontSize: 13,
                  color: tab === t.key ? 'var(--ink-0)' : 'var(--ink-3)',
                  fontWeight: tab === t.key ? 500 : 400,
                  borderBottom:
                    tab === t.key ? '2px solid var(--ink-0)' : '2px solid transparent',
                  cursor: 'pointer',
                  marginBottom: -1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 24, minHeight: 0 }}>
            {tab === 'source' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Panel title="Inbound payload">
                  <SourcePayload lead={lead} />
                </Panel>
                <Panel title="Auto-extracted">
                  <KV
                    pairs={[
                      ['Detected product', leadProduct(lead)],
                      ['Detected location', leadRegion(lead)],
                      [
                        'Detected urgency',
                        lead.conversationState ?? '—',
                      ],
                      [
                        'Spam score',
                        '—',
                      ],
                      ['Duplicate check', '—'],
                    ]}
                  />
                </Panel>
              </div>
            )}
            {tab === 'conversation' && (
              <Panel title="Conversation">
                <ChatTranscript messages={transcript} />
              </Panel>
            )}
            {tab === 'trail' && <VerificationTrail events={trail} />}
            {tab === 'activities' && <ActivityList events={activities} title="Activities" />}
            {tab === 'team-activity' && (
              <ActivityList events={teamActivities} title="Assigned team activity" />
            )}
            {tab === 'reassign' && (
              <Panel title="Reassignment history">
                {reassignHistory.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    No reassignments. Lead is on first assignment.
                  </div>
                ) : (
                  <div style={{ position: 'relative', paddingLeft: 16 }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: 5,
                        top: 4,
                        bottom: 4,
                        width: 1,
                        background: 'var(--line)',
                      }}
                    />
                    {reassignHistory.map((e, i) => (
                      <TrailItem key={e.id ?? i} e={e} first={i === 0} />
                    ))}
                  </div>
                )}
              </Panel>
            )}
          </div>
        </div>

        {/* Right action panel */}
        <div
          style={{
            width: 300,
            borderLeft: '1px solid var(--line)',
            background: 'var(--ivory-1)',
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
            flexShrink: 0,
          }}
        >
          <Avatar name={leadName(lead)} size={56} bg="var(--saffron)" />
          <div>
            <div
              style={{
                fontFamily: 'var(--ff-serif)',
                fontSize: 18,
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {leadName(lead)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              {leadProduct(lead)} · {leadRegion(lead)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button className="btn btn-primary" style={{ justifyContent: 'flex-start' }}>
              <Phone size={13} /> Call · {leadPhone(lead)}
            </button>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start' }}>
              <MessageSquare size={13} /> WhatsApp via ChatKart
            </button>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start' }}>
              <ArrowRight size={13} /> Reassign (admin approval)
            </button>
            <button
              className="btn btn-outline"
              style={{ justifyContent: 'flex-start', color: 'var(--danger)' }}
            >
              <X size={13} /> Reject lead
            </button>
          </div>
          {lead.assigneeName && (
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <FieldLabel>Current assignment</FieldLabel>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  background: 'var(--ivory-0)',
                  borderRadius: 6,
                  border: '1px solid var(--line)',
                }}
              >
                <Avatar name={lead.assigneeName} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{lead.assigneeName}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {lead.teamName ?? '—'}
                  </div>
                </div>
                <span className="chip chip-green">Active</span>
              </div>
            </div>
          )}
          <div>
            <FieldLabel>Tags</FieldLabel>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {leadProduct(lead) !== '—' && <span className="chip">{leadProduct(lead)}</span>}
              {leadRegion(lead) !== '—' && <span className="chip">{leadRegion(lead)}</span>}
              <span className={`chip chip-${priorityFrom(lead) === 'hot' ? 'red' : priorityFrom(lead) === 'warm' ? 'amber' : ''}`}>
                <PriorityDot p={priorityFrom(lead)} /> {priorityFrom(lead)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

function KV({ pairs }: { pairs: [string, string][] }) {
  return (
    <div style={{ fontSize: 12 }}>
      {pairs.map(([k, v], i) => (
        <div
          key={k}
          style={{
            display: 'flex',
            gap: 12,
            padding: '6px 0',
            borderBottom: i === pairs.length - 1 ? 'none' : '1px solid var(--ivory-3)',
          }}
        >
          <span style={{ width: 140, color: 'var(--ink-3)' }}>{k}</span>
          <span style={{ flex: 1, color: 'var(--ink-1)' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function SourcePayload({ lead }: { lead: Lead }) {
  const fields: [string, string][] = [
    ['Lead ID', lead.id],
    ['Source', lead.source ?? '—'],
    ['Phone', leadPhone(lead)],
    ['Email', lead.email ?? '—'],
    ['Product interest', leadProduct(lead)],
    ['Region', leadRegion(lead)],
    ['Conversation state', lead.conversationState ?? '—'],
    ['Created', lead.createdAt ?? '—'],
    ['Verified at', lead.verifiedAt ?? '—'],
    ['Verifier', lead.verifierName ?? '—'],
    ['Notes', lead.notes ?? lead.note ?? '—'],
  ];
  return (
    <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--ink-1)', lineHeight: 1.7 }}>
      {fields.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            gap: 10,
            borderBottom: '1px dashed var(--ivory-3)',
            padding: '3px 0',
          }}
        >
          <span style={{ width: 130, color: 'var(--ink-3)', flexShrink: 0 }}>{k}</span>
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function VerificationTrail({ events }: { events: TrailEvent[] }) {
  return (
    <Panel title="Verification trail">
      {events.length === 0 ? (
        <EmptyState title="No trail events" sub="Once verification actions happen, they'll appear here." />
      ) : (
        <div style={{ position: 'relative', paddingLeft: 16 }}>
          <div
            style={{
              position: 'absolute',
              left: 5,
              top: 4,
              bottom: 4,
              width: 1,
              background: 'var(--line)',
            }}
          />
          {events.map((e, i) => (
            <TrailItem key={e.id ?? i} e={e} first={i === 0} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function TrailItem({ e, first }: { e: TrailEvent; first: boolean }) {
  return (
    <div style={{ position: 'relative', paddingBottom: 14 }}>
      <span
        style={{
          position: 'absolute',
          left: -15,
          top: 4,
          width: 10,
          height: 10,
          borderRadius: 5,
          background: first ? 'var(--forest)' : 'var(--ink-3)',
          border: '2px solid var(--ivory-1)',
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontWeight: 500, fontSize: 13 }}>{e.what ?? '—'}</span>
        {e.who && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>· {e.who}</span>}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }} className="mono">
          {e.ago ?? (e.createdAt ? relativeTime(e.createdAt) : '')}
        </span>
      </div>
      {e.detail && (
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{e.detail}</div>
      )}
    </div>
  );
}

function ActivityList({ events, title }: { events: Activity[]; title: string }) {
  return (
    <Panel title={title}>
      {events.length === 0 ? (
        <EmptyState title="No activity yet" />
      ) : (
        events.map((e, i) => (
          <div
            key={e.id ?? i}
            style={{
              display: 'flex',
              gap: 12,
              padding: '10px 0',
              borderBottom: i === events.length - 1 ? 'none' : '1px solid var(--ivory-3)',
            }}
          >
            <Avatar name={e.who ?? '?'} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{e.who ?? '—'}</span> {e.what ?? ''}
              </div>
              {e.detail && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{e.detail}</div>
              )}
            </div>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }} className="mono">
              {e.ago ?? (e.createdAt ? relativeTime(e.createdAt) : '')}
            </span>
          </div>
        ))
      )}
    </Panel>
  );
}

function ChatTranscript({ messages }: { messages: ChatMsg[] }) {
  if (messages.length === 0) {
    return (
      <EmptyState title="No conversation" sub="Messages from WhatsApp / chat appear here once the lead engages." />
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxHeight: 480,
        overflowY: 'auto',
        paddingRight: 4,
      }}
    >
      {messages.map((m, i) => {
        const fromAura =
          (m.from ?? '').toLowerCase() === 'aura' ||
          (m.from ?? '').toLowerCase() === 'agent' ||
          (m.from ?? '').toLowerCase() === 'system';
        const text = m.text ?? m.body ?? '';
        return (
          <div
            key={m.id ?? i}
            style={{
              alignSelf: fromAura ? 'flex-start' : 'flex-end',
              maxWidth: '85%',
              background: fromAura ? 'var(--ivory-2)' : '#DCF8C6',
              color: 'var(--ink-1)',
              fontSize: 12,
              lineHeight: 1.45,
              padding: '7px 10px',
              borderRadius: 8,
              border: '1px solid ' + (fromAura ? 'var(--line)' : '#C8E5B6'),
            }}
          >
            <div>{text}</div>
            {m.createdAt && (
              <div
                style={{
                  fontSize: 9,
                  color: 'var(--ink-4)',
                  marginTop: 3,
                  textAlign: 'right',
                }}
              >
                {relativeTime(m.createdAt)} ago
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default LeadDetailPage;
